import { useQuery } from '@tanstack/react-query';
import { Banknote } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { useClientOptions } from './Invoices';
import { useCurrencies } from './Clients';
import { DownloadButton } from '../components/DownloadButton';
import { ReceiptLink } from '../components/ReceiptPicker';
import {
  Badge,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

/**
 * CR-001 §54 — the payments ledger.
 *
 * There is deliberately no "new payment" button here: §57 puts payment creation
 * behind `POST /invoices/:id/payments`, so a payment is always recorded from the
 * invoice it settles. The client and project filters below resolve through that
 * invoice too, rather than through columns a payment does not have.
 */
export function PaymentsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const clients = useClientOptions();
  const currencies = useCurrencies();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payments', page, filters],
    queryFn: () =>
      api.get<Paginated<any> & { byCurrency: any[]; totalLocalAmount: number; baseCurrency: string }>('/payments', {
        page,
        pageSize: 25,
        ...filters,
      }),
  });

  return (
    <>
      <PageHeader title={t('payments.title')} />

      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label={t('payments.collectedTotal')}
          value={formatMoney(data?.totalLocalAmount ?? 0, data?.baseCurrency, locale)}
          tone="success"
        />
        {(data?.byCurrency ?? []).slice(0, 3).map((row) => (
          <Metric
            key={row.currency}
            label={row.currency}
            value={formatMoney(row.amount, row.currency, locale)}
            hint={`${row.payments} ${t('payments.title').toLowerCase()}`}
          />
        ))}
      </div>

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          />
          <Select
            value={filters.clientId ?? ''}
            onChange={(e) => { setFilters({ ...filters, clientId: e.target.value }); setPage(1); }}
          >
            <option value="">{t('invoices.client')} — {t('common.all')}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
          <Select
            value={filters.currency ?? ''}
            onChange={(e) => { setFilters({ ...filters, currency: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <div className="flex items-center gap-4">
            <Input
              type="date"
              value={filters.from ?? ''}
              onChange={(e) => { setFilters({ ...filters, from: e.target.value }); setPage(1); }}
            />
            <Input
              type="date"
              value={filters.to ?? ''}
              onChange={(e) => { setFilters({ ...filters, to: e.target.value }); setPage(1); }}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <Checkbox
              checked={filters.includeReversed === 'true'}
              onChange={(e) => {
                setFilters({ ...filters, includeReversed: e.target.checked ? 'true' : '' });
                setPage(1);
              }}
              label={t('payments.includeReversed')}
            />
          </div>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Banknote className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('payments.number')}</Th>
                  <Th>{t('common.date')}</Th>
                  <Th>{t('invoices.client')}</Th>
                  <Th>{t('nav.invoices')}</Th>
                  <Th>{t('payments.method')}</Th>
                  <Th align="end">{t('common.amount')}</Th>
                  <Th align="end">{t('payments.exchangeRate')}</Th>
                  <Th align="end">{t('payments.localAmount')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <Tr
                    key={p.id}
                    className={p.reversedAt ? 'opacity-60' : undefined}
                    onClick={() => navigate(`/invoices/${p.invoice.id}`)}
                  >
                    <Td>
                      <span className="font-mono text-mono-sm">{p.number}</span>
                      {p.reversedAt && (
                        <Badge tone="error" className="ms-2">{t('payments.reversed')}</Badge>
                      )}
                    </Td>
                    <Td className="tabular text-muted">{formatDate(p.paymentDate, locale)}</Td>
                    <Td className="max-w-xs truncate">
                      {locale === 'ar' && p.invoice.client?.companyNameAr
                        ? p.invoice.client.companyNameAr
                        : p.invoice.client?.companyName}
                    </Td>
                    <Td><span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{p.invoice.number}</span></Td>
                    <Td className="text-muted">{p.paymentMethod.replace('_', ' ')}</Td>
                    <Td align="end" className="tabular">{formatMoney(p.amount, p.currency, locale)}</Td>
                    <Td align="end" className="tabular text-muted">{Number(p.exchangeRate)}</Td>
                    <Td align="end" className="tabular">{formatMoney(p.localAmount, p.localCurrency, locale)}</Td>
                    <Td align="end">
                      <span className="flex justify-end gap-2">
                        {p.attachment && <ReceiptLink file={p.attachment} size="sm" />}
                        <DownloadButton size="sm" path={`/payments/${p.id}/receipt`}>{t('payments.receipt')}</DownloadButton>
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
